import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

// `appearance-none` + explicit `border-solid` are required because Tailwind
// Preflight is intentionally not loaded (coexistence with legacy CSS); without
// them the native button bevel/border shows through.
const buttonVariants = cva(
  'inline-flex cursor-pointer appearance-none items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-solid border-transparent font-display text-sm font-bold leading-tight transition-[background,border-color,color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-55 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Signature prestige CTA: navy gradient, gold text, gold sheen.
        default:
          'bg-[image:var(--nq-grad-glow)] text-[color:var(--nq-gold-soft)] border-[color:var(--nq-glow-border)] shadow-[var(--nq-shadow-glow-btn)] hover:border-[color:var(--nq-glow-border-hover)]',
        // Secondary CTA: blue prestige glass — deep navy gradient, bright-cyan
        // text, blue glow border (high-contrast counterpart to the gold default).
        secondary:
          'bg-[image:var(--nq-grad-secondary)] text-[color:var(--nq-blue-bright)] border-[color:var(--nq-secondary-border)] shadow-[var(--nq-shadow-glow-btn)] hover:border-[color:var(--nq-secondary-border-hover)] hover:text-[color:var(--nq-blue-bright)]',
        // Orange CTA — warm gradient with a top sheen + halo, deep burnt-orange
        // label. Brightens on hover for a tactile lift.
        orange:
          'bg-[image:var(--nq-grad-orange)] text-[color:var(--nq-orange-text)] border-[color:var(--nq-orange-border-glow)] shadow-[var(--nq-shadow-orange)] transition-[filter,box-shadow] hover:brightness-105 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_2px_14px_rgba(234,106,12,0.5),0_0_24px_rgba(249,115,22,0.4)]',
        // Green CTA — solid gain-green gradient, deep-green label, green halo.
        green:
          'bg-[image:var(--nq-grad-green)] text-[color:var(--nq-green-text)] border-[color:var(--nq-green-border-glow)] shadow-[var(--nq-shadow-green)] transition-[filter,box-shadow] hover:brightness-105 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_2px_14px_rgba(22,177,75,0.5),0_0_24px_rgba(46,230,107,0.42)]',
        // Refined danger: red tint + red text/border (not a flat red fill).
        destructive:
          'bg-[color:var(--nq-destructive-soft)] text-[color:var(--nq-down-soft)] border-[color:var(--nq-destructive-border)] hover:bg-[color:var(--nq-destructive-soft-hover)]',
        // Outline: clear visible border + bright readable text, subtle fill on hover.
        outline:
          'bg-transparent text-[color:var(--nq-blue-bright)] border-[color:var(--nq-outline-border)] hover:bg-primary/10 hover:border-[color:var(--nq-outline-border-hover)] hover:text-[color:var(--nq-blue-bright)]',
        ghost:
          'bg-[color:var(--nq-slate-700)] text-[color:var(--nq-slate-50)] border-[color:var(--nq-slate-500)] hover:bg-[color:var(--nq-slate-600)]',
        // Prestige status CTA (e.g. "Outperforming S&P 500") — green glow pill.
        success:
          'bg-positive/22 text-[color:var(--nq-up-bright)] border-[color:var(--nq-return-up-border)] uppercase tracking-[0.04em] shadow-[0_0_16px_rgba(46,230,107,0.18)] hover:bg-positive/32',
        // Text link: bright cyan, underlined on hover, no button chrome.
        link: 'border-transparent bg-transparent font-semibold text-[color:var(--nq-blue-bright)] underline-offset-4 hover:underline hover:text-primary',
      },
      size: {
        default: 'h-10 px-4.5 py-3',
        sm: 'h-9 px-3.5 text-xs',
        lg: 'h-11 px-6',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
